import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import Blog from "./pages/Blog.tsx";
import BlogPost from "./pages/BlogPost.tsx";
import Footer from "./components/Footer.tsx";
import Navbar from "./components/Navbar.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import StickyBottomBar from "./components/StickyBottomBar.tsx";

const About = lazy(() => import("./pages/About.tsx"));
const PhysicalTraining = lazy(() => import("./pages/PhysicalTraining.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Retreats = lazy(() => import("./pages/Retreats.tsx"));
const Nutrition = lazy(() => import("./pages/Nutrition.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

const PageLayout = ({ children, fullBleed }: { children: React.ReactNode; fullBleed?: boolean }) => (
  <>
    <Navbar />
    <div className={fullBleed ? "" : "pt-[100px]"}>{children}</div>
    <div className="pb-[calc(3.75rem+env(safe-area-inset-bottom))]"><Footer /></div>
    <StickyBottomBar />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<PageLayout fullBleed><Index /></PageLayout>} />
            <Route path="/about" element={<PageLayout fullBleed><About /></PageLayout>} />
            <Route path="/physical-activity-training" element={<PageLayout fullBleed><PhysicalTraining /></PageLayout>} />
            <Route path="/nutrition" element={<PageLayout fullBleed><Nutrition /></PageLayout>} />
            <Route path="/retreats" element={<PageLayout fullBleed><Retreats /></PageLayout>} />
            <Route path="/blog" element={<PageLayout fullBleed><Blog /></PageLayout>} />
            <Route path="/blog/:slug" element={<PageLayout fullBleed><BlogPost /></PageLayout>} />
            <Route path="/contact" element={<PageLayout fullBleed><Contact /></PageLayout>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
